import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';
import { Container } from 'reactstrap';
import { Button, Loading, Alert } from '@whitelabel/component-library';
import {
  mediaBreakpointDown,
  mediaBreakpointUp,
  themeColor,
  backgroundStyles,
  backgroundImage,
  color,
  getThemeProp,
  fontStyle,
  buttonColor,
  borderRadius,
} from '@whitelabel/helpers/style';
import helpCenterImg from '../../media/banners/help-center.jpg';
import helpCenterMobileImg from '../../media/banners/help-center-mobile.jpg';
import StyledPrimaryTitle from '../../styled/StyledPrimaryTitle';

export const StyledBGContainer = styled.div<{ $rtl: boolean; $hideBottomBoxOnDesktop?: boolean }>`
  display: flex;
  align-items: center;
  min-height: 7.875rem;
  margin-top: -1.5rem;

  ${backgroundStyles}

  ${mediaBreakpointDown('sm')`
    ${backgroundImage(helpCenterMobileImg)}
  `}
  ${mediaBreakpointUp('md')`
    min-height: 9.125rem;
    margin-top: -2rem;

    ${backgroundImage(helpCenterImg)}
  `}

  ${mediaBreakpointUp('lg')`
    min-height: 13.75rem;
    margin-top: -3rem;
    ${({ $hideBottomBoxOnDesktop }: { $hideBottomBoxOnDesktop: boolean }) =>
      $hideBottomBoxOnDesktop &&
      css`
        margin-bottom: 3rem;
      `}
  `}
`;

export const StyledTitle = styled(StyledPrimaryTitle)`
  margin-bottom: 0;
`;

export const StyledBottomBox = styled.div<{ $hideOnDesktop?: boolean; $noMarginBottom?: boolean }>`
  margin-bottom: 1.5rem;
  background: ${themeColor('primary')};

  ${mediaBreakpointUp('md')`
    margin-bottom: 2rem;
  `}

  ${mediaBreakpointUp('lg')`
    ${({ $hideOnDesktop }: { $hideOnDesktop: boolean }) =>
      $hideOnDesktop &&
      css`
        display: none;
      `}
    margin-bottom: 3rem;
  `}

${({ $noMarginBottom }) =>
    $noMarginBottom &&
    css`
      margin-bottom: 0 !important;
    `}
`;

export const StyledLoginBlock = styled(Container)`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 1rem;
  padding-bottom: 1rem;
  text-align: center;

  ${mediaBreakpointUp('md')`
    flex-direction: row;
    justify-content: center;
  `}

  ${mediaBreakpointUp('lg')`
    padding-top: 1.5rem;
    padding-bottom: 1.5rem;
  `}

  div {
    ${fontStyle('paragraph')};
    margin-bottom: 1rem;

    ${mediaBreakpointUp('md')`
      margin-inline-end: 1.5rem;
      margin-bottom: 0;
      text-align: start;
    `}
  }

  h2 {
    margin-bottom: 0;
    text-transform: initial;

    ${mediaBreakpointUp('md')`
      display: inline-block;
      margin-inline-end: 0.5rem;
    `}
  }
`;

export const StyledPartnersBlock = styled.div`
  padding: 1rem 0;
  background-image: linear-gradient(
    ${color('artyClickSkyBlue')} 0,
    ${color('brightCerulean')} 50.52%,
    ${color('java')} 100%
  );

  ${mediaBreakpointUp('md')`
    padding: 1.5rem 0;
  `}

  ${mediaBreakpointUp('lg')`
    padding: 3rem 0;
  `}
`;

export const StyledWelcomeText = styled.h2<{ $hasPartners: boolean }>`
  color: ${color('white')};
  ${({ $hasPartners }) =>
    $hasPartners
      ? css`
          margin-bottom: 1rem;
          ${mediaBreakpointUp('md')`
            margin-bottom: 1.5rem;
          `}

          ${mediaBreakpointUp('lg')`
            margin-bottom: 3rem;
          `}
        `
      : css`
          margin-bottom: 0;
        `}
`;

export const StyledPartnersBox = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  ${mediaBreakpointUp('md')`
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.5rem;
  `}

  ${mediaBreakpointUp('lg')`
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 2rem;
  `}

  > div {
    background-color: ${color('white')};
    border-radius: ${borderRadius('base')};
    opacity: 20%;
  }
`;

export const StyledQuickActionsBox = styled(Container)`
  display: flex;
  justify-content: space-between;
  ${mediaBreakpointUp('md')`
    align-items: center;
  `}

  h3 {
    display: none;
    margin-bottom: 0;
    text-transform: initial;
    ${mediaBreakpointUp('md')`
      display: block;
    `}
  }

  ul {
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 0;
    padding: 0;
    list-style: none;

    ${mediaBreakpointUp('md')`
    flex-direction: row;
    width: initial;
    padding: 1rem 0;
  `}

    ${mediaBreakpointUp('lg')`
    padding: 1.5rem 0;
  `}

  li {
      padding: 1rem 0;
      border-inline-end: 0;
      line-height: 1;
      :not(:last-child) {
        border-bottom: ${getThemeProp('borderWidth')} solid ${themeColor('secondary')};
      }

      ${mediaBreakpointUp('md')`
        padding: 0.5rem 1rem;
        :not(:last-child) {
          border-bottom: 0;
          border-inline-end: ${getThemeProp('borderWidth')} solid ${themeColor('secondary')};
        }
        :last-child {  
          padding-inline-end: 0;
        }
      `}

      ${mediaBreakpointUp('lg')`
        padding-inline-start: 1.5rem;
        padding-inline-end: 1.5rem;
      `}
    }
  }
`;

export const StyledActionButton = styled(Button).attrs(() => ({
  buttonType: 'tertiary',
  tag: Link,
}))`
  flex-direction: row;
  width: 100%;
  white-space: normal;

  && svg {
    min-width: 1rem;
    margin-inline-end: 0.25rem;
    color: ${buttonColor('primary', 'label', 'default')};
  }
  :hover,
  :active,
  :focus {
    color: ${buttonColor('primary', 'label', 'hover')};

    && svg {
      color: ${buttonColor('primary', 'label', 'hover')};
    }
  }
`;

const marginTop = css`
  margin-top: 1rem;
  ${mediaBreakpointUp('md')`
    margin-top: 1.5rem;
  `}

  ${mediaBreakpointUp('lg')`
    margin-top: 3rem;
  `}
`;

export const StyledLoading = styled(Loading)`
  ${marginTop}
  height: 3.625rem;

  ${mediaBreakpointUp('lg')`
    height: 3.8125rem;
  `}
`;

export const StyledAlert = styled(Alert)`
  ${marginTop}
  margin-bottom: 0;
`;
